/**
 * INT-02 — categorias de denúncia.
 *
 * 2026-07-27 (migration 019): **12 categorias**. A autora dos documentos oficiais,
 * Jéssica Frazão (Compliance/SESMT), confirmou via Monday que são 12 e que a
 * faltante é exatamente "Violação do Código de Ética/Conduta" (updates 5377650154
 * de 2026-07-15 e 5405128265 de 2026-07-27, item "Ajustes no canal de denúncias").
 * Isso SUPERSEDE o racional "11" da migration 015 abaixo: a 015 lera a "12
 * categorias" da nota de revisão do POL-CMP-001 §14 como changelog e fixara a
 * tabela §5 (11) como normativa — a autora desfez essa contradição interna do doc
 * em favor de 12. `violacao_conduta_etica` (que a 015 dobrara em `outros`) VOLTA
 * como a 12ª, logo antes de `outros` (§5 não a posiciona; é a catch-all a última).
 *
 * 2026-07-14 (migration 015 — mantida como história): alinhou às tabelas dos docs
 * — POL-CMP-001 §5 / IT-CMP-001 §3 — então lidas como **11**, com **Corrupção e
 * Fraude SEPARADAS**. Superseou a decisão de 7 da 014 ("APENAS estas 7").
 * Mudanças que a 015 fez frente ao 014 (ainda valem, exceto o DROP abaixo):
 *   - SPLIT `fraude_ou_corrupcao` → `corrupcao` + `fraude` (separadas nos docs).
 *   - DROP `violacao_conduta_etica` → `outros`. ⚠️ REVERTIDO pela 019 (re-add) —
 *     o slug volta como 12ª categoria (dados antigos remapeados em `outros` na 015
 *     ficam como estão: história imutável, sem back-migration).
 *   - RE-ADD `discriminacao_salarial_genero`, `seguranca_saude`,
 *     `violacao_dados_lgpd`, `outros` (o 014 os removera).
 *
 * `discriminacao` permanece como bucket com `SUBCATEGORIAS_DISCRIMINACAO`
 * opcional (raça/gênero/religião/etc.). A categoria top-level
 * `discriminacao_salarial_genero` (salário-específica) e o sub-tipo `genero`
 * (gênero em geral) COEXISTEM por decisão do operador — não são o mesmo recorte.
 */
export const DENUNCIA_CATEGORIAS = [
  'assedio_moral',
  'assedio_sexual',
  'discriminacao',
  'discriminacao_salarial_genero',
  'violencia_no_trabalho',
  'corrupcao',
  'fraude',
  'conflito_interesse',
  'seguranca_saude',
  'violacao_dados_lgpd',
  'violacao_conduta_etica',
  'outros',
] as const;

export type DenunciaCategoria = (typeof DENUNCIA_CATEGORIAS)[number];

/**
 * Trilha 2a — sub-tipos opcionais para categorias de discriminação.
 *
 * Aplicável apenas quando `categoria` === `discriminacao`. O sub-tipo `genero`
 * cobre discriminação de gênero em geral; a categoria top-level
 * `discriminacao_salarial_genero` (salário-específica) é um recorte distinto —
 * as duas coexistem por decisão do operador (2026-07-14).
 * Multi-select de lista fechada — alinha com:
 *   - CF/88 art. 7º XXX (proibição de discriminação em salário/critério)
 *   - Lei 9.029/95 (proibição genérica de discriminação no trabalho)
 *   - Estatuto da Igualdade Racial (Lei 12.288/10)
 *   - LBI / Lei 13.146/15 (deficiência)
 *   - Estatuto do Idoso (Lei 10.741/03)
 *
 * "outro" é o escape para casos que não caibam (paridade com `outros` em
 * categoria, mas com nome no singular para evitar shadowing).
 */
export const SUBCATEGORIAS_DISCRIMINACAO = [
  'raca_etnia',
  'genero',
  'orientacao_sexual',
  'identidade_genero',
  'religiao',
  'deficiencia',
  'idade',
  'origem_nacional',
  'outro',
] as const;

export type SubcategoriaDiscriminacao = (typeof SUBCATEGORIAS_DISCRIMINACAO)[number];

/**
 * Roteamento organizacional (Política oficial): cada denúncia pertence a um
 * comitê de competência derivado da categoria. É CLASSIFICAÇÃO DERIVADA em
 * runtime — nunca persistida, nunca usada para restringir acesso (o acesso
 * universal do ADR-0001 permanece). Puramente informativa.
 *
 * A composição real do comitê ainda está em confirmação com o cliente (pode ser
 * 1 comitê único, não 2). Por isso o VALOR aqui é só uma etiqueta de competência
 * — o rótulo exibido é resolvido por i18n no app. Se a confirmação vier "1
 * comitê", troca-se o rótulo i18n sem refactor desta lógica.
 */
export type ComiteCompetencia = 'compliance' | 'rh' | 'sesmt';

/**
 * Mapa PARCIAL de propósito: `outros` fica FORA → `comiteCompetencia()` = null =
 * triagem/default (catch-all sem competência fixa). Destinos travados com o
 * operador (2026-07-14): corrupção/fraude/violação de dados/conflito → compliance
 * (compliance-jurídico); assédios/discriminação(+salarial)/violência → RH;
 * segurança e saúde → SESMT (saúde/segurança ocupacional).
 *
 * `violacao_conduta_etica` (12ª, migration 019) → **compliance**: o Código de
 * Ética/Conduta é instrumento de compliance/governança. Alinha com a clarificação
 * da Jéssica (Monday 2026-07-15, update 5377650154): a divisão Compliance/RH dos
 * documentos é "a responsabilidade organizacional de condução da apuração" — não
 * separação de acesso (o acesso universal do ADR-0001 permanece). Esta etiqueta é
 * DERIVADA em runtime, informativa, nunca persistida (ver `comiteCompetencia`).
 */
const COMPETENCIA_POR_CATEGORIA: Readonly<Partial<Record<DenunciaCategoria, ComiteCompetencia>>> = {
  corrupcao: 'compliance',
  fraude: 'compliance',
  conflito_interesse: 'compliance',
  violacao_dados_lgpd: 'compliance',
  violacao_conduta_etica: 'compliance',
  assedio_moral: 'rh',
  assedio_sexual: 'rh',
  discriminacao: 'rh',
  discriminacao_salarial_genero: 'rh',
  violencia_no_trabalho: 'rh',
  seguranca_saude: 'sesmt',
};

/**
 * Mapeia uma categoria para o comitê de competência.
 *
 * Aceita `string` (não `DenunciaCategoria`) porque o valor chega cru do servidor
 * em telas que tipam `categoria: string`. Slug sem competência mapeada (`outros`)
 * ou fora das 12 oficiais → `null`: a etiqueta some na UI (cai na triagem/default),
 * nunca rotula errado um caso. O guard `hasOwnProperty` evita cair em prop herdada
 * de `Object.prototype` (ex.: `'toString'`).
 */
export function comiteCompetencia(categoria: string): ComiteCompetencia | null {
  return Object.prototype.hasOwnProperty.call(COMPETENCIA_POR_CATEGORIA, categoria)
    ? (COMPETENCIA_POR_CATEGORIA[categoria as DenunciaCategoria] ?? null)
    : null;
}
